import { useEffect, useState, useCallback } from "react";
import {
  TextArea,
  Button,
  ButtonGroup,
} from "@datapay-by-datacom/design-system";
import s from "./ThreadMessageInput.module.css";
import { useLocation } from "react-router";
import { useNavigate } from "react-router";
import { ThreadInitStateSchema } from "../types/ThreadInitState";
import { OrgKey, OrgKeyCategory } from "../../generated/core-api";
import { ThreadKnowledgeSelector } from "./ThreadKnowledgeSelector";
import { UIThread } from "../../generated/core-api";
import { Disclaimer } from "../../support/components/Disclaimer";

interface ThreadMessageInputProps {
  handleSubmit: (content: string) => void;
  isReceiving: boolean;
  thread: UIThread; // Thread from parent component
  userOrgs: { id: string; category: OrgKeyCategory; name: string }[]; // Organizations from parent (required)
  setThreadKnowledge: (knowledgeFromOrgs: OrgKey[]) => Promise<void>;
}

/**
 * Combined hook to manage content state
 */
function useMessageInputState(location: ReturnType<typeof useLocation>) {
  const [content, setContent] = useState("");
  const navigate = useNavigate();

  // Helper function to initialize state from thread state
  const initializeFromState = useCallback(
    (state: { userIntent: string }) => {
      // Set content
      setContent(state.userIntent);

      // Clear location state to avoid re-initializing
      navigate(location.pathname, { replace: true });
      return true; // Signal that we handled initialization
    },
    [navigate, location.pathname]
  );

  // Main initialization effect
  useEffect(() => {
    // Parse thread init state
    const parsedState = ThreadInitStateSchema.safeParse(location.state);

    // Initialize from thread state if available
    if (parsedState.success) {
      initializeFromState(parsedState.data);
    }
  }, [location.state, initializeFromState]);

  // Return all the state and setters
  return {
    content,
    setContent,
  };
}

export function ThreadMessageInput({
  handleSubmit,
  isReceiving,
  thread,
  userOrgs,
  setThreadKnowledge,
}: ThreadMessageInputProps) {
  const [error, setError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);

  const location = useLocation();

  // Use hook for content management
  const { content, setContent } = useMessageInputState(location);

  const submitMessage = async (messageContent: string) => {
    setContent("");
    setIsSending(true);

    try {
      await handleSubmit(messageContent);
    } catch (error) {
      console.error(error);
      setError("Failed to send message");
      // Restore the content on error so user can retry
      setContent(messageContent);
    } finally {
      setIsSending(false);
    }
  };

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    await submitMessage(content);
  };

  // Handle Enter to submit (Shift+Enter for new line)
  const onKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      // Don't submit if disabled
      if (isSending || isReceiving || !content.trim()) {
        return;
      }
      submitMessage(content);
    }
  };

  return (
    <form
      aria-label="Message input"
      className={s.threadMessageInput}
      onSubmit={onSubmit}
    >
      <TextArea
        placeholder="Message (Enter to send)"
        rows={5}
        value={content}
        onInput={({ currentTarget }) => setContent(currentTarget.value)}
        onKeyDown={onKeyDown}
        style={{ width: "100%" }}
        aria-label="Message"
        autoFocus
      />

      <div className={s.controls}>
        <div className={s.controlsLeft}>
          <Disclaimer />
        </div>
        <ButtonGroup>
          <ThreadKnowledgeSelector
            thread={thread}
            setThreadKnowledge={setThreadKnowledge}
            userOrgs={userOrgs}
            isDisabled={isSending || isReceiving}
          />

          <Button
            type="submit"
            variant="primary"
            isDisabled={isSending || isReceiving || !content.trim()}
            aria-label="Send message"
          >
            {isSending ? "Sending..." : "Send"}
          </Button>
        </ButtonGroup>
      </div>

      {error && <div className={s.error}>{error}</div>}
    </form>
  );
}
